import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ChevronRight,
  Clock,
  Check,
  ArrowRight,
  ShoppingCart,
  RefreshCw,
  Leaf,
  Users,
  Zap,
  DollarSign,
  Heart,
  ChefHat,
  Sparkles
} from 'lucide-react';

/**
 * Harris Teeter Demo
 * 
 * White-label demo proving:
 * 1. This is not a meal kit
 * 2. This reduces decision fatigue
 * 3. This increases basket confidence inside Harris Teeter's ecosystem
 */

// Harris Teeter Brand Colors
const HT_RED = '#E31837';
const HT_DARK = '#1a1a1a';

// Demo meal data
const DEMO_MEALS = {
  'family-friendly': [
    { id: 1, name: 'Lemon Chicken with Roasted Veggies', time: 35, protein: 'Chicken Breast' },
    { id: 2, name: 'Turkey Taco Bowls', time: 25, protein: 'Ground Turkey' },
    { id: 3, name: 'Teriyaki Chicken Stir-Fry', time: 20, protein: 'Chicken Thighs' },
    { id: 4, name: 'Sheet-Pan Sausage & Potatoes', time: 40, protein: 'Italian Sausage' },
    { id: 5, name: 'Pasta with Marinara & Side Salad', time: 20, protein: 'None' },
  ],
  'weeknight-healthy': [
    { id: 1, name: 'Grilled Salmon with Asparagus', time: 25, protein: 'Salmon' },
    { id: 2, name: 'Mediterranean Chicken Bowl', time: 30, protein: 'Chicken Breast' },
    { id: 3, name: 'Shrimp & Vegetable Stir-Fry', time: 20, protein: 'Shrimp' },
    { id: 4, name: 'Turkey Lettuce Wraps', time: 15, protein: 'Ground Turkey' },
    { id: 5, name: 'Baked Cod with Roasted Broccoli', time: 25, protein: 'Cod' },
  ],
};

// Grocery list items
const GROCERY_ITEMS = [
  { name: 'Harris Teeter Chicken Breast', qty: '2 lbs', price: 8.99, htBrand: true },
  { name: 'Ground Turkey 93% Lean', qty: '1.25 lbs', price: 5.99, htBrand: false },
  { name: 'HT Organics Baby Spinach', qty: '5 oz', price: 3.49, htBrand: true, organic: true },
  { name: 'Harris Teeter Pasta', qty: '1 lb', price: 1.29, htBrand: true },
  { name: 'HT Italian Sausage', qty: '1 lb', price: 4.49, htBrand: true },
  { name: 'Yellow Onions', qty: '3 ct', price: 1.99, htBrand: false },
  { name: 'Bell Peppers', qty: '3 ct', price: 2.99, htBrand: false },
  { name: 'HT Marinara Sauce', qty: '24 oz', price: 2.49, htBrand: true },
  { name: 'Russet Potatoes', qty: '3 lbs', price: 3.49, htBrand: false },
  { name: 'HT Shredded Cheese', qty: '8 oz', price: 2.99, htBrand: true },
  { name: 'Fresh Broccoli', qty: '1 bunch', price: 2.49, htBrand: false },
  { name: 'Harris Teeter Rice', qty: '2 lbs', price: 2.99, htBrand: true },
];

const HarrisTeeterDemo = () => {
  const [screen, setScreen] = useState('landing');
  const [selectedPath, setSelectedPath] = useState(null);
  const [meals, setMeals] = useState([]);
  const [preferences, setPreferences] = useState({
    htBrand: true,
    organic: false,
    budget: false,
  });
  const [cartBuilding, setCartBuilding] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const paths = [
    { id: 'weeknight-healthy', name: 'Weeknight Healthy', desc: '30-minute meals', icon: Zap },
    { id: 'family-friendly', name: 'Family Friendly', desc: 'Crowd pleasers', icon: Users },
    { id: 'high-protein', name: 'High Protein', desc: 'Fuel your week', icon: Heart },
    { id: 'budget-friendly', name: 'Budget Friendly', desc: 'Save smart', icon: DollarSign },
    { id: 'low-prep', name: 'Low Prep', desc: 'Minimal effort', icon: Clock },
  ];

  const selectPath = (pathId) => {
    setSelectedPath(pathId);
    setMeals(DEMO_MEALS[pathId] || DEMO_MEALS['family-friendly']);
    setScreen('plan');
  };

  const swapMeal = (index) => {
    const newMeals = [...meals];
    const swaps = ['Grilled Tofu Bowl', 'Black Bean Tacos', 'Veggie Stir-Fry'];
    newMeals[index] = { 
      ...newMeals[index], 
      name: swaps[index % swaps.length],
      protein: 'Plant-Based'
    };
    setMeals(newMeals);
  };

  const buildCart = () => {
    setCartBuilding(true);
    setTimeout(() => {
      setCartBuilding(false);
      setScreen('cart');
    }, 1500);
  };

  const calculateTotal = () => {
    let total = GROCERY_ITEMS.reduce((sum, item) => sum + item.price, 0);
    if (preferences.htBrand) total -= 6.80;
    if (preferences.budget) total -= 4.20;
    return total.toFixed(2);
  };

  // Screen: Landing
  if (screen === 'landing') {
    return (
      <div className="min-h-screen bg-white" data-testid="ht-demo-landing">
        {/* Header */}
        <header className="border-b">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Harris Teeter Logo Mock */}
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: HT_RED }}
              >
                HT
              </div>
              <span className="font-semibold text-lg" style={{ color: HT_DARK }}>
                Harris Teeter
              </span>
            </div>
            <span className="text-xs text-gray-400">Meal Planning</span>
          </div>
        </header>

        {/* Hero */}
        <main className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: HT_DARK }}
          >
            Dinner, simplified.
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-xl mx-auto">
            Plan a full week of meals with ingredients you already trust from Harris Teeter.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              className="h-14 px-8 text-lg rounded-full"
              style={{ backgroundColor: HT_RED }}
              onClick={() => setScreen('paths')}
              data-testid="ht-plan-my-week"
            >
              Plan My Week
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <p className="mt-6 text-sm text-gray-500">
            Browse 
            <button className="text-gray-700 underline mx-1">prepared meals</button>
            instead
          </p>
        </main>

        {/* Trust Signals */}
        <div className="border-t bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 py-8">
            <div className="grid grid-cols-3 gap-6 text-center text-sm">
              <div>
                <p className="font-semibold text-gray-900">Not a meal kit</p>
                <p className="text-gray-500">Shop your way</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Harris Teeter prices</p>
                <p className="text-gray-500">Plus your VIC savings</p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">5 minutes</p>
                <p className="text-gray-500">Plan → Cart → Done</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-4 text-center text-xs text-gray-400">
          Internal demo  •  Meal planning powered by partner technology
        </footer>
      </div>
    );
  }

  // Screen: Path Selection
  if (screen === 'paths') {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="ht-demo-paths">
        <header className="bg-white border-b">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: HT_RED }}
            >
              HT
            </div>
            <span className="font-semibold" style={{ color: HT_DARK }}>Meal Planning</span>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold mb-2" style={{ color: HT_DARK }}>
              How do you want your week to feel?
            </h2>
            <p className="text-gray-600">
              Pick a style. We&apos;ll build 5 dinners around it.
            </p>
          </div>

          <div className="space-y-3">
            {paths.map((path) => (
              <button
                key={path.id}
                onClick={() => selectPath(path.id)}
                className="w-full bg-white border rounded-xl p-5 flex items-center justify-between hover:border-gray-400 transition-colors text-left"
                data-testid={`ht-path-${path.id}`}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${HT_RED}15` }}
                  >
                    <path.icon className="h-6 w-6" style={{ color: HT_RED }} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{path.name}</p>
                    <p className="text-sm text-gray-500">{path.desc}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  // Screen: Weekly Plan
  if (screen === 'plan') {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="ht-demo-plan">
        <header className="bg-white border-b">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: HT_RED }}
              >
                HT
              </div>
              <span className="font-semibold" style={{ color: HT_DARK }}>Your Week</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {paths.find(p => p.id === selectedPath)?.name}
            </Badge>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-1" style={{ color: HT_DARK }}>
              5 Dinners, Ready to Shop
            </h2>
            <p className="text-sm text-gray-600">
              Flexible meals with overlapping ingredients to reduce waste.
            </p>
          </div>

          <div className="space-y-3 mb-8">
            {meals.map((meal, index) => (
              <Card key={meal.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500 uppercase">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][index]}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900">{meal.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {meal.time} min
                        </span>
                        <span>{meal.protein}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => swapMeal(index)}
                      className="text-xs"
                      data-testid={`ht-swap-${index}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Swap
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Button 
            className="w-full h-14 text-lg rounded-xl"
            style={{ backgroundColor: HT_RED }}
            onClick={buildCart}
            disabled={cartBuilding}
            data-testid="ht-build-cart"
          >
            {cartBuilding ? (
              <>
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                Building your cart...
              </>
            ) : (
              <>
                <ShoppingCart className="h-5 w-5 mr-2" />
                Build My Harris Teeter Cart
              </>
            )}
          </Button>
        </main>
      </div>
    );
  }

  // Screen: Cart
  if (screen === 'cart') {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="ht-demo-cart">
        <header className="bg-white border-b">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: HT_RED }}
              >
                HT
              </div>
              <span className="font-semibold" style={{ color: HT_DARK }}>Your Cart</span>
            </div>
            <Badge className="bg-green-100 text-green-700 border-0">
              <Check className="h-3 w-3 mr-1" />
              {GROCERY_ITEMS.length} items
            </Badge>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-8">
          {/* Preferences Toggle */}
          <button
            onClick={() => setPreferencesOpen(!preferencesOpen)}
            className="w-full mb-6 p-4 bg-white border rounded-xl flex items-center justify-between"
            data-testid="ht-preferences-toggle"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5" style={{ color: HT_RED }} />
              <span className="font-medium">Shopping Preferences</span>
            </div>
            <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${preferencesOpen ? 'rotate-90' : ''}`} />
          </button>

          {preferencesOpen && (
            <Card className="mb-6">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Prefer Harris Teeter Brand</p>
                    <p className="text-xs text-gray-500">Lower prices, same quality</p>
                  </div>
                  <Switch 
                    checked={preferences.htBrand}
                    onCheckedChange={(v) => setPreferences({...preferences, htBrand: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Organic When Available</p>
                    <p className="text-xs text-gray-500">HT Organics products</p>
                  </div>
                  <Switch 
                    checked={preferences.organic}
                    onCheckedChange={(v) => setPreferences({...preferences, organic: v})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Budget-Friendly Options</p>
                    <p className="text-xs text-gray-500">Maximize savings</p>
                  </div>
                  <Switch 
                    checked={preferences.budget}
                    onCheckedChange={(v) => setPreferences({...preferences, budget: v})}
                  />
                </div>
                
                {(preferences.htBrand || preferences.budget) && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-green-600 font-medium">
                      {preferences.htBrand && preferences.budget 
                        ? '6 items updated • Estimated total ↓ $11.00'
                        : preferences.htBrand 
                          ? '3 items updated • Estimated total ↓ $6.80'
                          : '2 items updated • Estimated total ↓ $4.20'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Grocery List */}
          <div className="bg-white border rounded-xl overflow-hidden mb-6">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Grocery List</span>
                <span className="text-xs text-gray-500">Pantry basics excluded</span>
              </div>
            </div>
            <div className="divide-y">
              {GROCERY_ITEMS.map((item, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border-2 border-gray-300" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.name}
                        {item.htBrand && (
                          <Badge 
                            variant="outline" 
                            className="ml-2 text-xs py-0"
                            style={{ borderColor: HT_RED, color: HT_RED }}
                          >
                            HT
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{item.qty}</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium">${item.price.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <Card className="mb-6" style={{ backgroundColor: `${HT_RED}08` }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Estimated Total</p>
                  <p className="text-xs text-gray-500">VIC savings applied at checkout</p>
                </div>
                <p className="text-2xl font-bold" style={{ color: HT_DARK }}>
                  ${calculateTotal()}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Checkout Button */}
          <Button 
            className="w-full h-14 text-lg rounded-xl"
            style={{ backgroundColor: HT_RED }}
            onClick={() => setScreen('checkout')}
            data-testid="ht-checkout"
          >
            Send to Harris Teeter Checkout
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>

          <p className="text-center text-xs text-gray-500 mt-4">
            Complete your order in the Harris Teeter app or website
          </p>
        </main>
      </div>
    );
  }

  // Screen: Checkout Confirmation
  if (screen === 'checkout') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="ht-demo-checkout">
        <div className="max-w-md mx-auto px-6 text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${HT_RED}15` }}
          >
            <Check className="h-10 w-10" style={{ color: HT_RED }} />
          </div>
          
          <h2 className="text-2xl font-bold mb-2" style={{ color: HT_DARK }}>
            Cart Ready!
          </h2>
          <p className="text-gray-600 mb-8">
            Your {GROCERY_ITEMS.length} items are ready in your Harris Teeter cart. 
            Complete checkout with your VIC card for additional savings.
          </p>

          <div className="space-y-3">
            <Button 
              className="w-full h-12 rounded-xl"
              style={{ backgroundColor: HT_RED }}
            >
              Continue in Harris Teeter App
            </Button>
            <Button 
              variant="outline"
              className="w-full h-12 rounded-xl"
              onClick={() => setScreen('landing')}
            >
              Plan Another Week
            </Button>
          </div>

          <div className="mt-12 p-4 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-900 mb-2">What just happened:</p>
            <ul className="text-xs text-gray-600 text-left space-y-1">
              <li>✓ 5 dinners planned in 2 minutes</li>
              <li>✓ Ingredients consolidated & deduplicated</li>
              <li>✓ Harris Teeter products prioritized</li>
              <li>✓ Pantry basics excluded</li>
              <li>✓ Ready for VIC savings at checkout</li>
            </ul>
          </div>
        </div>

        <footer className="fixed bottom-4 left-0 right-0 text-center text-xs text-gray-400">
          Internal demo  •  Meal planning powered by partner technology
        </footer>
      </div>
    );
  }

  return null;
};

export default HarrisTeeterDemo;
